import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import {
  getProgressEntries,
  addProgressEntry,
  deleteProgressEntry,
  uploadProgressPhoto,
  deleteProgressPhoto,
  getWeightSettings,
  saveWeightSettings
} from '../utils/syncService';

const MyProgress = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCompareMode, setShowCompareMode] = useState(false);
  const [showComparePicker, setShowComparePicker] = useState(false);
  const [compareBeforeId, setCompareBeforeId] = useState(null);
  const [compareAfterId, setCompareAfterId] = useState(null);
  const [compareBeforePhotoIndex, setCompareBeforePhotoIndex] = useState(0);
  const [compareAfterPhotoIndex, setCompareAfterPhotoIndex] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [startingWeight, setStartingWeight] = useState('');
  const [targetWeight, setTargetWeight] = useState('');
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [newEntry, setNewEntry] = useState({
    date: new Date().toISOString().split('T')[0],
    weight: '',
    notes: '',
    photos: []
  });
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadData();
  }, []);

  const loadData = async () => {
    const userEmail = localStorage.getItem('userEmail');
    if (!userEmail) { setLoading(false); return; }
    try {
      const entriesResult = await getProgressEntries(userEmail);
      if (entriesResult.success) setEntries(entriesResult.data);
      const settingsResult = await getWeightSettings(userEmail);
      if (settingsResult.success && settingsResult.data) {
        setStartingWeight(settingsResult.data.starting_weight || '');
        setTargetWeight(settingsResult.data.target_weight || '');
      }
    } catch (error) {
      console.error('Error loading progress data:', error);
    } finally {
      setLoading(false);
    }
  };

  const addSelectedFiles = (files) => {
    if (!files || files.length === 0) return;
    const newPhotos = [...newEntry.photos, ...files];
    setNewEntry({ ...newEntry, photos: newPhotos });
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreviews((prev) => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files || []);
    addSelectedFiles(files);
    e.target.value = '';
  };

  const removePhoto = (index) => {
    const newPhotos = newEntry.photos.filter((_, i) => i !== index);
    setNewEntry({ ...newEntry, photos: newPhotos });
    setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddEntry = async () => {
    const userEmail = localStorage.getItem('userEmail');
    if (!userEmail) {
      alert('Please log in to save');
      return;
    }
    setUploading(true);
    try {
      const photoUrls = [];
      for (let i = 0; i < newEntry.photos.length; i++) {
        const file = newEntry.photos[i];
        const uploadResult = await uploadProgressPhoto(userEmail, file);
        if (!uploadResult.success) {
          throw new Error(uploadResult.error || 'Photo upload failed');
        }
        photoUrls.push(uploadResult.url);
      }
      const entryData = {
        date: newEntry.date,
        weight: newEntry.weight ? parseFloat(newEntry.weight) : null,
        photoUrl: photoUrls.length > 0 ? JSON.stringify(photoUrls) : null,
        notes: newEntry.notes
      };
      const result = await addProgressEntry(userEmail, entryData);
      if (result.success) {
        setEntries(prev => [result.data, ...prev].sort((a, b) => new Date(b.date) - new Date(a.date)));
        setShowAddModal(false);
        setNewEntry({ date: new Date().toISOString().split('T')[0], weight: '', notes: '', photos: [] });
        setPhotoPreviews([]);
        localStorage.setItem('lastWeightEntry', Date.now().toString());
        window.dispatchEvent(new Event('weightUpdated'));
      } else {
        console.error('Failed to save progress entry:', result);
        alert('Failed to save entry: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving progress entry:', error);
      alert('Error saving: ' + (error?.message || 'Unknown error'));
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteEntry = async (entry) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      if (entry.photo_url) {
        try {
          const photos = JSON.parse(entry.photo_url);
          if (Array.isArray(photos)) {
            for (const url of photos) await deleteProgressPhoto(url);
          }
        } catch (e) {
          await deleteProgressPhoto(entry.photo_url);
        }
      }
      const result = await deleteProgressEntry(entry.id);
      if (result.success) setEntries(prev => prev.filter(e => e.id !== entry.id));
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const saveSettings = async () => {
    const userEmail = localStorage.getItem('userEmail');
    if (!userEmail) return;
    await saveWeightSettings(userEmail, { goalType: 'track', startingWeight, targetWeight });
    setShowSettingsModal(false);
  };

  const getEntryPhotos = (entry) => {
    if (!entry.photo_url) return [];
    try {
      const parsed = JSON.parse(entry.photo_url);
      return Array.isArray(parsed) ? parsed : [entry.photo_url];
    } catch (e) {
      return [entry.photo_url];
    }
  };

  const weightEntries = entries.filter(e => e.weight).slice(0, 12).reverse();
  const maxWeight = weightEntries.length > 0 ? Math.max(...weightEntries.map(e => e.weight)) : 100;
  const minWeight = weightEntries.length > 0 ? Math.min(...weightEntries.map(e => e.weight)) : 0;
  const weightRange = maxWeight - minWeight || 10;
  const latestWeight = entries.find(e => e.weight)?.weight;
  const weightChange = latestWeight && startingWeight ? (latestWeight - parseFloat(startingWeight)).toFixed(1) : null;
  const entriesWithPhotos = entries.filter(e => e.photo_url);
  const photoEntries = [...entriesWithPhotos].sort((a, b) => new Date(b.date) - new Date(a.date));
  const defaultBeforeEntry = photoEntries[photoEntries.length - 1];
  const defaultAfterEntry = photoEntries[0];
  const compareBeforeEntry = photoEntries.find(e => String(e.id) === String(compareBeforeId)) || defaultBeforeEntry;
  const compareAfterEntry = photoEntries.find(e => String(e.id) === String(compareAfterId)) || defaultAfterEntry;
  const beforePhotos = compareBeforeEntry ? getEntryPhotos(compareBeforeEntry) : [];
  const afterPhotos = compareAfterEntry ? getEntryPhotos(compareAfterEntry) : [];
  const beforeIndex = beforePhotos.length > 0 ? Math.min(Math.max(0, compareBeforePhotoIndex), beforePhotos.length - 1) : 0;
  const afterIndex = afterPhotos.length > 0 ? Math.min(Math.max(0, compareAfterPhotoIndex), afterPhotos.length - 1) : 0;
  const beforePhotoUrl = beforePhotos[beforeIndex];
  const afterPhotoUrl = afterPhotos[afterIndex];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sage-50 via-beige-50 to-rose-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 via-beige-50 to-rose-50 pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <RouterLink to="/dashboard" className="inline-flex items-center gap-2 text-charcoal-500 hover:text-sage-600 text-sm mb-2">
                ← Back to Dashboard
              </RouterLink>
              <h1 className="text-3xl font-playfair font-semibold text-charcoal-900">My Progress</h1>
              <p className="text-charcoal-500 mt-1">Track your transformation journey</p>
            </div>
            <button type="button" onClick={() => setShowSettingsModal(true)} className="p-3 bg-white/80 rounded-xl shadow-soft text-charcoal-500 hover:text-purple-600">
              ⚙️
            </button>
          </div>
        </div>

        {(latestWeight || entries.length > 0) && (
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-white/80 rounded-2xl p-4 shadow-soft border border-sage-100">
              <div className="text-xs text-charcoal-500 mb-1">⚖️ Current</div>
              <div className="text-2xl font-bold text-charcoal-800">{latestWeight || '—'}</div>
              <div className="text-xs text-charcoal-400">kg</div>
            </div>
            <div className="bg-white/80 rounded-2xl p-4 shadow-soft border border-sage-100">
              <div className="text-xs text-charcoal-500 mb-1">🎯 Target</div>
              <div className="text-2xl font-bold text-purple-600">{targetWeight || '—'}</div>
              <div className="text-xs text-charcoal-400">kg</div>
            </div>
            <div className="bg-white/80 rounded-2xl p-4 shadow-soft border border-sage-100">
              <div className="text-xs text-charcoal-500 mb-1">📊 Change</div>
              <div className="text-2xl font-bold text-charcoal-800">
                {weightChange ? (parseFloat(weightChange) > 0 ? '+' : '') + weightChange : '—'}
              </div>
              <div className="text-xs text-charcoal-400">kg</div>
            </div>
          </div>
        )}

        <div className="flex gap-3 mb-6">
          <button type="button" onClick={() => setShowAddModal(true)} className="flex-1 bg-gradient-to-r from-purple-500 to-violet-500 text-white py-4 rounded-2xl font-semibold shadow-lg flex items-center justify-center gap-2">
            + Add Entry
          </button>
          {photoEntries.length >= 2 && (
            <button
              type="button"
              onClick={() => {
                setCompareBeforeId(defaultBeforeEntry?.id ?? null);
                setCompareAfterId(defaultAfterEntry?.id ?? null);
                setCompareBeforePhotoIndex(0);
                setCompareAfterPhotoIndex(0);
                setShowComparePicker(true);
              }}
              className="px-6 bg-white/80 text-charcoal-700 py-4 rounded-2xl font-medium border border-purple-200 shadow-soft"
            >
              ✨ Compare
            </button>
          )}
        </div>

        {weightEntries.length > 1 && (
          <div className="bg-white/80 rounded-2xl p-5 shadow-soft border border-sage-100 mb-6">
            <h2 className="font-semibold text-charcoal-800 mb-4">Weight Journey</h2>
            <div className="relative h-36">
              <div className="absolute left-0 top-0 bottom-0 w-8 flex flex-col justify-between text-xs text-charcoal-400">
                <span>{maxWeight.toFixed(0)}</span>
                <span>{minWeight.toFixed(0)}</span>
              </div>
              <div className="ml-10 h-full relative">
                <svg className="absolute inset-0 w-full h-full overflow-visible">
                  <polyline fill="none" stroke="#a855f7" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
                    points={weightEntries.map((entry, i) => {
                      const x = (i / (weightEntries.length - 1)) * 100;
                      const y = 100 - ((entry.weight - minWeight) / weightRange) * 100;
                      return `${x},${y}`;
                    }).join(' ')}
                  />
                </svg>
                {weightEntries.map((entry, i) => {
                  const x = (i / (weightEntries.length - 1)) * 100;
                  const y = 100 - ((entry.weight - minWeight) / weightRange) * 100;
                  return <div key={entry.id} className="absolute w-3 h-3 bg-white border-2 border-purple-500 rounded-full" style={{ left: `${x}%`, top: `${y}%`, transform: 'translate(-50%, -50%)' }} />;
                })}
              </div>
            </div>
          </div>
        )}

        {entriesWithPhotos.length > 0 && (
          <div className="mb-6">
            <h2 className="font-semibold text-charcoal-800 mb-3">Photo Gallery</h2>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {entriesWithPhotos.slice(0, 8).map((entry) => {
                const photos = getEntryPhotos(entry);
                return (
                  <div key={entry.id} onClick={() => setSelectedEntry(entry)} className="aspect-square relative rounded-2xl overflow-hidden cursor-pointer group shadow-soft">
                    <img src={photos[0]} alt="" className="w-full h-full object-cover" />
                    {photos.length > 1 && <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-0.5 rounded-full">+{photos.length - 1}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {entries.length > 0 && (
          <div>
            <h2 className="font-semibold text-charcoal-800 mb-3">All Entries</h2>
            <div className="space-y-3">
              {entries.map((entry) => {
                const photos = getEntryPhotos(entry);
                return (
                  <div key={entry.id} className="bg-white/80 rounded-xl p-4 shadow-soft border border-sage-100 cursor-pointer" onClick={() => setSelectedEntry(entry)}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {photos.length > 0 ? <img src={photos[0]} alt="" className="w-14 h-14 rounded-xl object-cover" /> : <div className="w-14 h-14 rounded-xl bg-purple-100 flex items-center justify-center">📊</div>}
                        <div>
                          <div className="font-medium text-charcoal-800">{new Date(entry.date).toLocaleDateString('en-NZ', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</div>
                          <div className="text-sm text-charcoal-500">{entry.weight && <span className="text-purple-600 font-medium">{entry.weight} kg</span>}{photos.length > 0 && ` • ${photos.length} photo${photos.length > 1 ? 's' : ''}`}</div>
                        </div>
                      </div>
                      <button type="button" onClick={(e) => { e.stopPropagation(); handleDeleteEntry(entry); }} className="p-2 text-charcoal-300 hover:text-red-400">🗑️</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {entries.length === 0 && (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">📸</div>
            <h3 className="text-xl font-semibold text-charcoal-800 mb-2">Start Your Journey</h3>
            <p className="text-charcoal-500">Add your first progress entry</p>
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl w-full sm:max-w-lg max-h-[85vh] overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white border-b border-sage-100 px-5 py-4 flex items-center justify-between rounded-t-3xl">
              <h3 className="text-lg font-semibold text-charcoal-900">New Progress Entry</h3>
              <button type="button" onClick={() => { setShowAddModal(false); setPhotoPreviews([]); setNewEntry({ ...newEntry, photos: [] }); }} className="w-8 h-8 flex items-center justify-center rounded-full bg-sage-100 text-charcoal-500">✕</button>
            </div>
            <div className="p-5 space-y-5">
              <div>
                <label className="block text-sm font-medium text-charcoal-700 mb-2">Photos</label>
                {photoPreviews.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {photoPreviews.map((preview, index) => (
                      <div key={index} className="relative aspect-square rounded-xl overflow-hidden">
                        <img src={preview} alt="" className="w-full h-full object-cover" />
                        <button type="button" onClick={() => removePhoto(index)} className="absolute top-1 right-1 w-6 h-6 bg-black/50 text-white rounded-full text-sm">×</button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => cameraInputRef.current && cameraInputRef.current.click()}
                    className="w-full py-4 border-2 border-dashed border-purple-200 rounded-xl text-purple-500 hover:bg-purple-50"
                  >
                    � Take photo
                  </button>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    className="w-full py-4 border-2 border-dashed border-purple-200 rounded-xl text-purple-500 hover:bg-purple-50"
                  >
                    🖼️ Add from gallery
                  </button>
                </div>

                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handlePhotoSelect}
                  className="sr-only"
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoSelect}
                  className="sr-only"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-charcoal-700 mb-2">Weight (kg)</label>
                <input type="number" step="0.1" value={newEntry.weight} onChange={(e) => setNewEntry({ ...newEntry, weight: e.target.value })} className="w-full p-3.5 bg-sage-50 border border-sage-200 rounded-xl" placeholder="68.5" />
              </div>
              <div>
                <label className="block text-sm font-medium text-charcoal-700 mb-2">Date</label>
                <input type="date" value={newEntry.date} onChange={(e) => setNewEntry({ ...newEntry, date: e.target.value })} className="w-full p-3.5 bg-sage-50 border border-sage-200 rounded-xl" />
              </div>
              <div>
                <label className="block text-sm font-medium text-charcoal-700 mb-2">Notes</label>
                <textarea value={newEntry.notes} onChange={(e) => setNewEntry({ ...newEntry, notes: e.target.value })} className="w-full p-3.5 bg-sage-50 border border-sage-200 rounded-xl resize-none" rows="2" placeholder="How are you feeling?" />
              </div>
              <button type="button" onClick={handleAddEntry} disabled={uploading} className="w-full py-4 bg-gradient-to-r from-purple-500 to-violet-500 text-white rounded-xl font-semibold disabled:opacity-50">
                {uploading ? 'Saving...' : 'Save Entry'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showComparePicker && compareBeforeEntry && compareAfterEntry && (
        <div className="fixed inset-0 bg-black/40 z-50 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="min-h-full flex items-start justify-center p-4">
            <div className="bg-white rounded-3xl w-full sm:max-w-lg shadow-xl overflow-hidden flex flex-col my-4">
              <div className="border-b border-sage-100 px-6 py-4 flex items-center justify-between flex-shrink-0">
                <h3 className="text-lg font-semibold text-charcoal-900">Choose photos to compare</h3>
                <button type="button" onClick={() => setShowComparePicker(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-sage-100 text-charcoal-500">✕</button>
              </div>

              <div className="p-4 sm:p-6 space-y-5">
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-purple-600 mb-2">BEFORE</div>
                    <select
                      value={compareBeforeId ?? ''}
                      onChange={(e) => {
                        setCompareBeforeId(e.target.value);
                        setCompareBeforePhotoIndex(0);
                      }}
                      className="w-full p-2.5 sm:p-3 bg-sage-50 border border-sage-200 rounded-xl text-xs sm:text-sm"
                    >
                      {photoEntries.map((entry) => (
                        <option key={entry.id} value={entry.id}>
                          {new Date(entry.date).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' })}
                          {entry.weight ? ` • ${entry.weight} kg` : ''}
                        </option>
                      ))}
                    </select>

                    <div className="mt-3 aspect-square sm:aspect-[3/4] rounded-2xl overflow-hidden bg-charcoal-100 flex items-center justify-center">
                      {beforePhotoUrl ? (
                        <img src={beforePhotoUrl} alt="Before" className="max-w-full max-h-full object-contain" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-charcoal-400">No photo</div>
                      )}
                    </div>

                    {beforePhotos.length > 1 && (
                      <div className="mt-3 overflow-x-auto">
                        <div className="flex gap-2">
                          {beforePhotos.map((url, idx) => (
                            <button
                              key={url}
                              type="button"
                              onClick={() => setCompareBeforePhotoIndex(idx)}
                              className={`w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 border ${idx === beforeIndex ? 'border-purple-500' : 'border-sage-200'}`}
                            >
                              <img src={url} alt="" className="w-full h-full object-cover" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-violet-600 mb-2">AFTER</div>
                    <select
                      value={compareAfterId ?? ''}
                      onChange={(e) => {
                        setCompareAfterId(e.target.value);
                        setCompareAfterPhotoIndex(0);
                      }}
                      className="w-full p-2.5 sm:p-3 bg-sage-50 border border-sage-200 rounded-xl text-xs sm:text-sm"
                    >
                      {photoEntries.map((entry) => (
                        <option key={entry.id} value={entry.id}>
                          {new Date(entry.date).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' })}
                          {entry.weight ? ` • ${entry.weight} kg` : ''}
                        </option>
                      ))}
                    </select>

                    <div className="mt-3 aspect-square sm:aspect-[3/4] rounded-2xl overflow-hidden bg-charcoal-100 flex items-center justify-center">
                      {afterPhotoUrl ? (
                        <img src={afterPhotoUrl} alt="After" className="max-w-full max-h-full object-contain" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-charcoal-400">No photo</div>
                      )}
                    </div>

                    {afterPhotos.length > 1 && (
                      <div className="mt-3 overflow-x-auto">
                        <div className="flex gap-2">
                          {afterPhotos.map((url, idx) => (
                            <button
                              key={url}
                              type="button"
                              onClick={() => setCompareAfterPhotoIndex(idx)}
                              className={`w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 border ${idx === afterIndex ? 'border-violet-500' : 'border-sage-200'}`}
                            >
                              <img src={url} alt="" className="w-full h-full object-cover" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {String(compareBeforeEntry?.id) === String(compareAfterEntry?.id) && (
                  <div className="text-sm text-red-500">Please choose two different entries.</div>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowComparePicker(false)}
                    className="flex-1 py-3.5 bg-sage-100 text-charcoal-600 rounded-xl font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={String(compareBeforeEntry?.id) === String(compareAfterEntry?.id)}
                    onClick={() => {
                      setShowComparePicker(false);
                      setShowCompareMode(true);
                    }}
                    className="flex-1 py-3.5 bg-gradient-to-r from-purple-500 to-violet-500 text-white rounded-xl font-medium disabled:opacity-50"
                  >
                    Compare
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCompareMode && compareBeforeEntry && compareAfterEntry && String(compareBeforeEntry.id) !== String(compareAfterEntry.id) && (
        <div className="fixed inset-0 bg-black z-50 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between p-4">
            <button type="button" onClick={() => setShowCompareMode(false)} className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white">✕</button>
            <h3 className="font-semibold text-white">Your Transformation ✨</h3>
            <button
              type="button"
              onClick={() => {
                setShowCompareMode(false);
                setShowComparePicker(true);
              }}
              className="h-10 px-3 rounded-full bg-white/10 text-white text-sm font-medium"
            >
              Edit
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="grid grid-cols-2 gap-2 p-2 sm:p-0">
              <div className="bg-charcoal-800 flex flex-col min-h-0">
              <div className="flex-1 min-h-0 flex items-center justify-center p-2 overflow-hidden">
                {beforePhotoUrl ? (
                  <img src={beforePhotoUrl} alt="Before" className="max-w-full max-h-full object-contain" />
                ) : (
                  <div className="text-white/70 text-sm">No photo</div>
                )}
              </div>

              {beforePhotos.length > 1 && (
                <div className="px-3 pb-3 overflow-x-auto">
                  <div className="flex gap-2">
                    {beforePhotos.map((url, idx) => (
                      <button
                        key={`${url}-${idx}`}
                        type="button"
                        onClick={() => setCompareBeforePhotoIndex(idx)}
                        className={`w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 border ${idx === beforeIndex ? 'border-purple-400' : 'border-white/20'}`}
                      >
                        <img src={url} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="px-3 pb-3">
                <div className="bg-white/95 rounded-2xl p-3">
                  <div className="text-xs text-purple-600 font-semibold">BEFORE</div>
                  <div className="text-sm font-semibold">{new Date(compareBeforeEntry.date).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                  {compareBeforeEntry.weight && <div className="text-sm text-charcoal-600">{compareBeforeEntry.weight} kg</div>}
                </div>
              </div>
            </div>

              <div className="bg-charcoal-800 flex flex-col min-h-0">
              <div className="flex-1 min-h-0 flex items-center justify-center p-2 overflow-hidden">
                {afterPhotoUrl ? (
                  <img src={afterPhotoUrl} alt="After" className="max-w-full max-h-full object-contain" />
                ) : (
                  <div className="text-white/70 text-sm">No photo</div>
                )}
              </div>

              {afterPhotos.length > 1 && (
                <div className="px-3 pb-3 overflow-x-auto">
                  <div className="flex gap-2">
                    {afterPhotos.map((url, idx) => (
                      <button
                        key={`${url}-${idx}`}
                        type="button"
                        onClick={() => setCompareAfterPhotoIndex(idx)}
                        className={`w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 border ${idx === afterIndex ? 'border-violet-400' : 'border-white/20'}`}
                      >
                        <img src={url} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="px-3 pb-3">
                <div className="bg-white/95 rounded-2xl p-3">
                  <div className="text-xs text-violet-600 font-semibold">AFTER</div>
                  <div className="text-sm font-semibold">{new Date(compareAfterEntry.date).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                  {compareAfterEntry.weight && <div className="text-sm text-charcoal-600">{compareAfterEntry.weight} kg</div>}
                </div>
              </div>
            </div>
          </div>
          </div>

          {compareBeforeEntry.weight && compareAfterEntry.weight && (
            <div className="p-4 bg-gradient-to-r from-purple-500 to-violet-500 text-center text-white">
              Total change: <span className="font-bold">{compareAfterEntry.weight - compareBeforeEntry.weight > 0 ? '+' : ''}{(compareAfterEntry.weight - compareBeforeEntry.weight).toFixed(1)} kg</span>
            </div>
          )}
        </div>
      )}

      {selectedEntry && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-4" onClick={() => setSelectedEntry(null)}>
          <div className="bg-white rounded-3xl w-full sm:max-w-lg max-h-[85vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-sage-100 px-5 py-4 flex items-center justify-between rounded-t-3xl">
              <h3 className="text-lg font-semibold">{new Date(selectedEntry.date).toLocaleDateString('en-NZ', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</h3>
              <button type="button" onClick={() => setSelectedEntry(null)} className="w-8 h-8 flex items-center justify-center rounded-full bg-sage-100">✕</button>
            </div>
            <div className="p-5">
              {getEntryPhotos(selectedEntry).length > 0 && (
                <div className="grid gap-2 mb-4" style={{ gridTemplateColumns: getEntryPhotos(selectedEntry).length === 1 ? '1fr' : '1fr 1fr' }}>
                  {getEntryPhotos(selectedEntry).map((url, i) => <img key={i} src={url} alt="" className="w-full aspect-[3/4] object-cover rounded-2xl" />)}
                </div>
              )}
              {selectedEntry.weight && <div className="p-3 bg-purple-50 rounded-xl mb-3"><span className="text-charcoal-500">Weight:</span> <span className="font-semibold">{selectedEntry.weight} kg</span></div>}
              {selectedEntry.notes && <div className="text-charcoal-600 bg-sage-50 rounded-xl p-4">{selectedEntry.notes}</div>}
            </div>
          </div>
        </div>
      )}

      {showSettingsModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-xl">
            <h3 className="text-xl font-semibold text-charcoal-900 mb-5">Weight Settings</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-charcoal-700 mb-2">Starting Weight (kg)</label>
                <input type="number" step="0.1" value={startingWeight} onChange={(e) => setStartingWeight(e.target.value)} className="w-full p-3.5 bg-sage-50 border border-sage-200 rounded-xl" placeholder="75" />
              </div>
              <div>
                <label className="block text-sm font-medium text-charcoal-700 mb-2">Target Weight (kg)</label>
                <input type="number" step="0.1" value={targetWeight} onChange={(e) => setTargetWeight(e.target.value)} className="w-full p-3.5 bg-sage-50 border border-sage-200 rounded-xl" placeholder="65" />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button type="button" onClick={() => setShowSettingsModal(false)} className="flex-1 py-3.5 bg-sage-100 text-charcoal-600 rounded-xl font-medium">Cancel</button>
              <button type="button" onClick={saveSettings} className="flex-1 py-3.5 bg-gradient-to-r from-purple-500 to-violet-500 text-white rounded-xl font-medium">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyProgress;
